import { useMutation } from "@apollo/client/react";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useForm } from "@tanstack/react-form";
import { useCallback, useState } from "react";
import { Button, Form, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { reportTypeLabels } from "../model/report";
import {
  CreateReportDocument,
  type CreateReportMutation,
  type ReportInput,
  type ReportType,
} from "../types/graphql/graphql";

type ValidationErrorDetail = NonNullable<
  NonNullable<NonNullable<CreateReportMutation["createReport"]>["errors"]>[number]
>;

type ReportFormProps = {
  onSubmit: () => void;
  onCancel: () => void;
  campsiteId: number;
};

export function ReportForm({ onSubmit, onCancel, campsiteId }: ReportFormProps) {
  const { t } = useTranslation();
  const [submitState, setSubmitState] = useState<{ errors?: ValidationErrorDetail[]; success: boolean } | undefined>(
    undefined,
  );
  const defaultReport: ReportInput = {
    type: "OCCUPIED",
    reporterEmail: "",
    reporterName: "",
    spamCheck: "",
    comment: "",
  };

  const notEmptyValidation = useCallback(
    ({ value }: { value: string | undefined | null }) => (value ? undefined : t("errors.messages.required")),
    [t],
  );
  const [createReportMutation] = useMutation(CreateReportDocument);

  const form = useForm({
    defaultValues: defaultReport,
    onSubmit: async ({ value: report, formApi }) => {
      const result = await createReportMutation({ variables: { report, campsiteId } });
      const errors = result.data?.createReport?.errors;
      if (errors) {
        setSubmitState({ success: false, errors });
      } else {
        setSubmitState({ success: true });
        formApi.reset();
        onSubmit?.();
      }
    },
  });

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();

        if (form.state.isFormValid) {
          form.handleSubmit();
        }
      }}
    >
      <Stack gap={3}>
        <form.Field name="type">
          {(field) => (
            <Form.Group>
              <Form.Label>{t("activerecord.attributes.report.type")}</Form.Label>
              {Object.entries(reportTypeLabels).map(([key, labelKey]) => (
                <Form.Check
                  key={key}
                  value={key}
                  id={key}
                  name={field.name}
                  onChange={(e) => field.handleChange(e.target.value as ReportType)}
                  checked={field.state.value === key}
                  type="radio"
                  label={t(labelKey)}
                />
              ))}
            </Form.Group>
          )}
        </form.Field>
        <form.Field name="reporterName" validators={{ onBlur: notEmptyValidation }}>
          {(field) => (
            <Form.Group controlId={field.name}>
              <Form.Label>{t("activerecord.attributes.report.reporter_name")}</Form.Label>
              <Form.Control
                name={field.name}
                value={field.state.value ?? undefined}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                isInvalid={!field.state.meta.isValid}
              />
              <Form.Control.Feedback type="invalid">{field.state.meta.errors.join(", ")}</Form.Control.Feedback>
            </Form.Group>
          )}
        </form.Field>
        <form.Field name="reporterEmail" validators={{ onBlur: notEmptyValidation }}>
          {(field) => (
            <Form.Group controlId={field.name}>
              <Form.Label>{t("activerecord.attributes.report.reporter_email")}</Form.Label>
              <Form.Control
                type="email"
                name={field.name}
                value={field.state.value ?? undefined}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                isInvalid={!field.state.meta.isValid}
              />
              <Form.Control.Feedback type="invalid">{field.state.meta.errors.join(", ")}</Form.Control.Feedback>
            </Form.Group>
          )}
        </form.Field>
        <form.Field name="spamCheck">
          {(field) => (
            <Form.Group controlId={field.name}>
              <Form.Label>{t("activerecord.attributes.report.spam_check")}</Form.Label>
              <Form.Control
                name={field.name}
                value={field.state.value ?? undefined}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                isInvalid={
                  !field.state.meta.isValid || submitState?.errors?.some((error) => error.attribute === "spam_check")
                }
              />
              <Form.Control.Feedback type="invalid">
                {[
                  ...field.state.meta.errors,
                  ...(submitState?.errors
                    ?.filter((error) => error.attribute === "spam_check")
                    ?.map((error) => error.message) || []),
                ].join(", ")}
              </Form.Control.Feedback>
            </Form.Group>
          )}
        </form.Field>
        <form.Field name="comment">
          {(field) => (
            <Form.Group controlId={field.name}>
              <Form.Label>{t("activerecord.attributes.report.comment")}</Form.Label>
              <Form.Control
                as="textarea"
                name={field.name}
                value={field.state.value ?? undefined}
                onChange={(e) => field.handleChange(e.target.value)}
              />
            </Form.Group>
          )}
        </form.Field>

        <Stack className="justify-content-end" direction="horizontal" gap={3}>
          <Button variant="secondary" onClick={onCancel}>
            {t("cancel")}
          </Button>
          <Button type="submit">
            {t("send")} <FontAwesomeIcon size="sm" icon={faChevronRight} />
          </Button>
        </Stack>
      </Stack>
    </Form>
  );
}
